import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button, FormField, EmptyState, Modal, FilterBar } from '../../components/ui';
import SearchableSelect from '../../components/SearchableSelect';
import DataTable from '../../components/DataTable';
import ExcelCalcForm from './ExcelCalcForm';

// ── Inline Lucide-tarzı SVG ikonlar ────────────────────────
const IconRefresh = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>);
const IconUser    = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>);
const IconPlus    = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>);
const IconBack    = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>);
const IconEdit    = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>);
const IconTrash   = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>);

const pad = (n) => String(n).padStart(2, '0');
const nowDateParts = () => {
  const d = new Date();
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};
const splitDateParts = (iso) => {
  if (!iso) return nowDateParts();
  const d = new Date(iso);
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

const fmtDate = (s) => s ? new Date(s).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
const fmtMoney = (n) => (n == null) ? '—' : new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(n);
const fmtCurrency = (n, code) => (n == null || !isFinite(n)) ? '—'
  : new Intl.NumberFormat('tr-TR', { style: 'currency', currency: code, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtPercent = (n) => (n == null || !isFinite(n)) ? '—'
  : new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' %';

// Listede USD/EUR/KAR % kolonları için snapshot'tan değer çek
const parseCalcData = (raw) => {
  if (!raw) return null;
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
};
const findSummaryValue = (cd, label) => {
  const arr = cd?.summary;
  if (!Array.isArray(arr)) return null;
  const it = arr.find(s => s.label === label);
  return (it && typeof it.value === 'number') ? it.value : null;
};

export default function PergolaCalculatorPage() {
  const [view, setView] = useState('list'); // 'list' | 'form'
  const [editingId, setEditingId] = useState(null);
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  // Filtreler — satış faturası listesi tarzında
  const [filters, setFilters] = useState({ date_from: '', date_to: '', account_id: '', template_id: '', transfer_status: 'all' });
  const [filtersOpen, setFiltersOpen] = useState(false);  // açılır/kapanır
  const [accountsLookup, setAccountsLookup] = useState([]);
  const [templatesLookup, setTemplatesLookup] = useState([]);
  // Çoklu seçim → teklife aktar
  const [bulkTransferIds, setBulkTransferIds] = useState(null);  // null | [ids]
  const [bulkClearFn, setBulkClearFn] = useState(null);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get('/pergola/calculations');
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingList(false);
    }
  }, []);

  // Lookup'lar: cari (account) ve şablon (template)
  useEffect(() => {
    api.get('/pergola/lookups').then(r => {
      const d = r.data || {};
      setAccountsLookup(d.accounts || []);
      setTemplatesLookup(d.templates || []);
    }).catch(() => {});
  }, []);

  useEffect(() => { if (view === 'list') loadList(); }, [view, loadList]);

  const openNew = () => { setEditingId(null); setView('form'); };
  const openEdit = (row) => { setEditingId(row.id); setView('form'); };
  const backToList = () => { setEditingId(null); setView('list'); };

  // Filtreli liste (client-side)
  const filteredList = list.filter(r => {
    if (filters.date_from && r.doc_date && r.doc_date.slice(0, 10) < filters.date_from) return false;
    if (filters.date_to   && r.doc_date && r.doc_date.slice(0, 10) > filters.date_to)   return false;
    if (filters.account_id && r.account_id !== filters.account_id) return false;
    if (filters.template_id && r.template_id !== filters.template_id) return false;
    if (filters.transfer_status === 'transferred'     && !r.converted_to_quote_id) return false;
    if (filters.transfer_status === 'not_transferred' && r.converted_to_quote_id)  return false;
    return true;
  });
  const hasActiveFilter = filters.date_from || filters.date_to || filters.account_id || filters.template_id || (filters.transfer_status && filters.transfer_status !== 'all');
  const clearFilters = () => setFilters({ date_from: '', date_to: '', account_id: '', template_id: '', transfer_status: 'all' });

  const remove = async (row) => {
    if (row.converted_to_quote_id) {
      alert(`Bu kayıt "${row.converted_to_quote_doc_no}" no'lu teklife aktarılmış — silinemez. Önce teklifi silin.`);
      return;
    }
    if (!window.confirm(`"${row.doc_no}" no'lu kayıt silinsin mi?`)) return;
    try {
      await api.delete('/pergola/calculations/' + row.id);
      await loadList();
    } catch (e) {
      alert(e.response?.data?.error || 'Silme başarısız');
    }
  };

  const openEditWithGuard = (row) => {
    // Sadece görüntüleme moduna geç — backend zaten PUT'u engelliyor
    setEditingId(row.id); setView('form');
  };

  // Form artık modal/overlay olarak açılır — liste arka planda kalır

  // ── LISTE GÖRÜNÜMÜ ──────────────────────────────────────
  return (
    <div className="stocks-page">
      <div className="stocks-topbar">
        <div className="stocks-topbar-left">
          <h2>Persan Maliyet Tabloları</h2>
          <span className="stocks-count-badge">
            {hasActiveFilter ? `${filteredList.length} / ${list.length}` : list.length} teklif
          </span>
        </div>
        <div className="stocks-topbar-right">
          <Button size="sm" icon={<IconPlus />} onClick={openNew}>Yeni Teklif</Button>
        </div>
      </div>

      {/* Filtre çubuğu — satış faturası listesi ile aynı (FilterBar — chevron toggle) */}
      <FilterBar
        show={filtersOpen}
        onToggle={() => setFiltersOpen(v => !v)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'date_from', type: 'date', label: 'İlk Tarih' },
          { key: 'date_to',   type: 'date', label: 'Son Tarih' },
          { key: 'account_id', type: 'select', label: 'Cari', flex: '1 1 240px', minWidth: 200,
            options: [{ value: '', label: 'Tüm Cariler' }, ...accountsLookup.map(a => ({ value: a.id, label: (a.code ? a.code + ' - ' : '') + a.name }))] },
          { key: 'template_id', type: 'select', label: 'Şablon', flex: '1 1 220px', minWidth: 180,
            options: [{ value: '', label: 'Tüm Şablonlar' }, ...templatesLookup.map(t => ({ value: t.id, label: t.product_name }))] },
          { key: 'transfer_status', type: 'select', label: 'Aktarım Durumu',
            options: [
              { value: 'all',             label: 'Tümü' },
              { value: 'transferred',     label: 'Aktarılanlar' },
              { value: 'not_transferred', label: 'Aktarılmayanlar' },
            ] },
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="pergola-calculations-list-v2"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loadingList}
          emptyText=""
          data={filteredList}
          rowKey="id"
          onRowClick={openEdit}
          bulkActions={[
            {
              key: 'transfer-to-quote',
              label: '📄 Verilen Teklife Aktar',
              variant: 'primary',
              onClick: (ids, clear) => { setBulkTransferIds(ids); setBulkClearFn(() => clear); },
            },
          ]}
          columns={[
            { id: 'doc_no', header: 'Belge No', accessor: 'doc_no', sortable: true, width: 130,
              cell: r => <strong className="stock-code">{r.doc_no}</strong> },
            { id: 'transfer_status', header: 'Aktarım',
              accessor: r => r.converted_to_quote_id ? 'Aktarıldı' : 'Aktarılmadı',
              sortable: true, width: 140,
              cell: r => r.converted_to_quote_id ? (
                <span title={`Teklif: ${r.converted_to_quote_doc_no}`} style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                  background: 'rgba(22,163,74,0.12)', color: '#16a34a',
                }}>✓ Aktarıldı{r.converted_to_quote_doc_no ? ' (' + r.converted_to_quote_doc_no + ')' : ''}</span>
              ) : (
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                  background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)',
                }}>Aktarılmadı</span>
              ) },
            { id: 'doc_date', header: 'Tarih', accessor: 'doc_date', sortable: true, width: 160,
              cell: r => <span className="stock-secondary">{fmtDate(r.doc_date)}</span> },
            { id: 'customer', header: 'Müşteri',
              accessor: r => r.account_title || r.manual_account_name || '',
              sortable: true, width: 240,
              cell: r => <span className="stock-name-cell">
                {r.account_title || r.manual_account_name || <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
              </span> },
            { id: 'phone', header: 'Telefon', accessor: 'manual_phone', sortable: true, width: 140,
              cell: r => <span className="stock-secondary">{r.manual_phone || '-'}</span> },
            { id: 'email', header: 'E-posta', accessor: 'manual_email', sortable: true, width: 200,
              cell: r => <span className="stock-secondary">{r.manual_email || '-'}</span> },
            { id: 'template', header: 'Şablon',
              accessor: 'template_name',
              sortable: true, width: 180,
              cell: r => <span className="stock-secondary">{r.template_name || '-'}</span> },
            { id: 'total', header: 'Satış (TL)',
              accessor: r => parseFloat(r.total_amount) || 0,
              sortable: true, width: 140,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: r => <span className="stock-price-cell">{fmtMoney(r.total_amount)}</span> },
            { id: 'usd', header: 'Satış (USD)',
              accessor: r => findSummaryValue(parseCalcData(r.calculation_data), 'DOLAR SATIŞ') || 0,
              sortable: true, width: 130,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: r => <span className="stock-secondary">{fmtCurrency(findSummaryValue(parseCalcData(r.calculation_data), 'DOLAR SATIŞ'), 'USD')}</span> },
            { id: 'eur', header: 'Satış (EUR)',
              accessor: r => findSummaryValue(parseCalcData(r.calculation_data), 'EURO SATIŞ') || 0,
              sortable: true, width: 130,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: r => <span className="stock-secondary">{fmtCurrency(findSummaryValue(parseCalcData(r.calculation_data), 'EURO SATIŞ'), 'EUR')}</span> },
            { id: 'profit', header: 'Kar %',
              accessor: r => findSummaryValue(parseCalcData(r.calculation_data), 'KAR %') || 0,
              sortable: true, width: 100,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: r => <span className="stock-secondary">{fmtPercent(findSummaryValue(parseCalcData(r.calculation_data), 'KAR %'))}</span> },
            { id: 'prepared_by', header: 'Hazırlayan', accessor: 'prepared_by_name', sortable: true, width: 160,
              cell: r => <span className="stock-secondary">{r.prepared_by_name || '-'}</span> },
            // Düzenle/Sil butonları listeden kaldırıldı — satıra tıklayınca modal açılır, oradan yönetilir.
          ]}
        />
        {!loadingList && list.length === 0 && (
          <div style={{ padding: 24 }}>
            <EmptyState
              title="Henüz teklif yok"
              description="Pergole hesabı yapmak için sağ üstteki Yeni Teklif butonuna tıklayın."
              compact
            />
          </div>
        )}
      </div>

      {/* Persan Maliyet form modal (yeni/düzenle) */}
      {view === 'form' && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          zIndex: 1500, display: 'flex', flexDirection: 'column',
          padding: 'clamp(4px, 2vw, 24px)',
        }}>
          <div style={{
            background: 'var(--bg-card)', borderRadius: 12, overflow: 'hidden',
            display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
            boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
            border: '1px solid var(--border-secondary)',
          }}>
            <PergolaForm
              id={editingId}
              row={list.find(x => x.id === editingId)}
              onBack={backToList}
              onSaved={backToList}
              onDelete={async () => {
                const row = list.find(x => x.id === editingId);
                if (!row) return;
                await remove(row);
                backToList();
              }}
            />
          </div>
        </div>
      )}

      {/* Çoklu Teklife Aktarım Modal */}
      {bulkTransferIds && (
        <MultiTransferToQuoteModal
          ids={bulkTransferIds}
          rows={list.filter(r => bulkTransferIds.includes(r.id))}
          onClose={() => setBulkTransferIds(null)}
          onTransferred={() => { setBulkTransferIds(null); if (bulkClearFn) bulkClearFn(); loadList(); }}
        />
      )}
    </div>
  );
}

// ─── Çoklu Verilen Teklife Aktarım Modal ─────────────────────────
function MultiTransferToQuoteModal({ ids, rows, onClose, onTransferred }) {
  const [currency, setCurrency] = useState('TRY');
  const [exchangeRate, setExchangeRate] = useState(1);
  const [manualRate, setManualRate] = useState(false);
  const [rateLoading, setRateLoading] = useState(false);
  const [vatIncluded, setVatIncluded] = useState(false);
  const [vatRate, setVatRate] = useState(20);
  const [warehouseId, setWarehouseId] = useState('');
  const [warehouses, setWarehouses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  // Depo listesi
  useEffect(() => {
    api.get('/warehouses').then(r => {
      const arr = r.data?.success ? (r.data.data?.items || r.data.data || []) : [];
      setWarehouses(arr);
      // Varsayılan: ilk depo veya kullanıcı default'u (settings'ten bilmek zor — ilk depo'yu seç)
      if (arr.length > 0 && !warehouseId) setWarehouseId(arr[0].id);
    }).catch(() => {});
  }, []);  // eslint-disable-line

  // Tüm seçili kayıtlarda secili para birimi için fiyat var mı?
  const findVal = (row, label) => {
    let cd;
    try { cd = typeof row.calculation_data === 'string' ? JSON.parse(row.calculation_data) : row.calculation_data; } catch { return null; }
    const arr = cd?.summary;
    if (!Array.isArray(arr)) return null;
    const it = arr.find(s => s.label === label);
    return (it && typeof it.value === 'number') ? it.value : null;
  };
  const labelFor = (c) => c === 'TRY' ? 'SATIŞ FİYATI' : c === 'USD' ? 'DOLAR SATIŞ' : 'EURO SATIŞ';
  const missing = rows.filter(r => {
    const v = findVal(r, labelFor(currency));
    if (currency === 'TRY' && (v == null || v <= 0)) return !(parseFloat(r.total_amount) > 0);
    return v == null || v <= 0;
  });

  useEffect(() => {
    setError(''); setManualRate(false);
    if (currency === 'TRY') { setExchangeRate(1); return; }
    setRateLoading(true);
    (async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const r = await api.get('/exchange/rates/lookup', { params: { date: today, currency_code: currency } });
        if (r.data.success && r.data.data) {
          const d = r.data.data;
          const rate = parseFloat(d.mid_rate) || parseFloat(d.selling_rate) || parseFloat(d.buying_rate) || parseFloat(d.rate);
          if (rate > 0) setExchangeRate(rate);
          else { setExchangeRate(0); setManualRate(true); setError(`${currency} için kur 0 — manuel girin`); }
        } else { setExchangeRate(0); setManualRate(true); setError(`${currency} kur bulunamadı — manuel girin`); }
      } catch { setExchangeRate(0); setManualRate(true); setError('Kur sorgulanamadı — manuel girin'); }
      finally { setRateLoading(false); }
    })();
  }, [currency]);

  const submit = async () => {
    if (missing.length > 0) { setError(`${missing.length} kaydın seçili para biriminde fiyatı yok`); return; }
    if (currency !== 'TRY' && (!exchangeRate || exchangeRate <= 0)) { setError('Geçerli bir kur girin'); return; }
    setSubmitting(true); setError('');
    try {
      const r = await api.post('/pergola/calculations/transfer-to-quote-multi', {
        ids,
        currency_code: currency,
        exchange_rate: manualRate ? exchangeRate : undefined,
        vat_included: vatIncluded,
        vat_rate: (() => { const v = parseFloat(vatRate); return isNaN(v) ? 20 : v; })(),
        warehouse_id: warehouseId || null,
      });
      if (r.data.success) setResult(r.data.data);
      else setError(r.data.message || 'Aktarım başarısız');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Aktarım başarısız');
    } finally { setSubmitting(false); }
  };

  if (result) {
    return (
      <Modal title="✓ Verilen Teklif Oluşturuldu" size="sm" onClose={onTransferred}
        closeOnOverlay={false} primaryAction={{ label: 'Tamam', onClick: onTransferred }}>
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ padding: '10px 14px', background: 'rgba(22,163,74,0.1)', borderRadius: 8, fontSize: '0.88rem' }}>
            <strong>Belge No:</strong> <code style={{ background: 'var(--bg-secondary)', padding: '2px 6px', borderRadius: 4 }}>{result.document_number || result.quote_id}</code>
          </div>
          <div style={{ fontSize: '0.85rem' }}>
            <div><strong>{result.line_count}</strong> kalem oluşturuldu.</div>
            <div>Para birimi: <strong>{result.currency_code}</strong> · Kur: <strong>{parseFloat(result.exchange_rate).toFixed(4)}</strong></div>
            <div>Genel Toplam: <strong>{parseFloat(result.grand_total).toFixed(2)} {result.currency_code}</strong></div>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={`${ids.length} Maliyet → Tek Teklif`}
      size="sm"
      onClose={() => !submitting && onClose()}
      closeOnOverlay={false}
      primaryAction={{
        label: 'Teklifi Oluştur', onClick: submit, loading: submitting,
        disabled: missing.length > 0 || (currency !== 'TRY' && !exchangeRate),
      }}
    >
      <div style={{ display: 'grid', gap: 10 }}>
        {error && <div className="ui-field-error" style={{ padding: 8, background: 'rgba(220,38,38,0.08)', borderRadius: 6 }}>{error}</div>}
        <FormField label="Para Birimi">
          <select value={currency} onChange={e => setCurrency(e.target.value)}>
            <option value="TRY">TRY (TL)</option>
            <option value="USD">USD ($)</option>
            <option value="EUR">EUR (€)</option>
          </select>
        </FormField>
        {currency !== 'TRY' && (
          <FormField label={`Kur (1 ${currency} = ? TL)`}
            hint={rateLoading ? 'Yükleniyor...' : (manualRate ? 'Manuel' : 'Otomatik')}>
            <input type="number" step="0.0001" value={exchangeRate}
              onChange={e => { setExchangeRate(parseFloat(e.target.value) || 0); setManualRate(true); }} />
          </FormField>
        )}
        <FormField label="KDV Oranı">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={vatRate} onChange={e => setVatRate(parseFloat(e.target.value))} style={{ flex: 1 }}>
              <option value={0}>%0 (KDV'siz)</option>
              <option value={1}>%1</option>
              <option value={10}>%10</option>
              <option value={20}>%20</option>
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={vatIncluded} onChange={e => setVatIncluded(e.target.checked)} />
              KDV Dahil
            </label>
          </div>
        </FormField>
        <FormField label="Depo" hint="Teklif başlığı ve kalemlerine yazılır">
          <select value={warehouseId} onChange={e => setWarehouseId(e.target.value)}>
            <option value="">— Seçiniz —</option>
            {warehouses.map(w => (
              <option key={w.id} value={w.id}>{w.code ? w.code + ' · ' : ''}{w.name}</option>
            ))}
          </select>
        </FormField>

        {/* Seçilen kayıtlar özet */}
        <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border-secondary)', borderRadius: 6 }}>
          <table className="zebra" style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Belge</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Cari</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Şablon</th>
                <th style={{ textAlign: 'right', padding: '6px 8px' }}>Fiyat ({currency})</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const v = findVal(r, labelFor(currency)) ?? (currency === 'TRY' ? parseFloat(r.total_amount) : null);
                const ok = v != null && v > 0;
                return (
                  <tr key={r.id} style={{ color: ok ? undefined : 'var(--danger)' }}>
                    <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{r.doc_no}</td>
                    <td style={{ padding: '4px 8px' }}>{r.account_title || r.manual_account_name || '—'}</td>
                    <td style={{ padding: '4px 8px' }}>{r.template_name || '—'}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace' }}>
                      {ok ? new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(v) : 'YOK'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {missing.length > 0 && (
          <div style={{ fontSize: '12px', color: 'var(--danger)' }}>
            ⚠ {missing.length} kayıtta {currency} fiyatı yok — aktarılamaz.
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────
// FORM (Yeni / Düzenle)
// ─────────────────────────────────────────────────────────────────
function PergolaForm({ id, row, onBack, onSaved, onDelete }) {
  const { user: currentUser } = useAuth();
  const initParts = nowDateParts();
  const [docNo, setDocNo] = useState('');
  const [docDateOnly, setDocDateOnly] = useState(initParts.date);
  const [docTime, setDocTime] = useState(initParts.time);
  // accountId: cari (account) VEYA potansiyel cari (prospect) ID'si — birleşik liste
  // selectedIsProspect: true ise prospect_accounts'tan, false ise accounts'tan; kayıtta account_id'yi
  // sadece gerçek cari için yazıyoruz, prospect ise account_id=null + manual alanlar.
  const [accountId, setAccountId] = useState(null);
  const [selectedIsProspect, setSelectedIsProspect] = useState(false);
  const [manual, setManual] = useState({ name: '', phone: '', address: '', email: '' });
  // Yeni teklifte: kullanıcı bir personele bağlıysa otomatik gelsin (kullanıcı değiştirebilir)
  const [preparedById, setPreparedById] = useState(id ? null : (currentUser?.personnelId || null));
  const [templateId, setTemplateId] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [calcResult, setCalcResult] = useState(null); // { values, salesPrice }
  const [initialCalcValues, setInitialCalcValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingProspect, setSavingProspect] = useState(false);
  const [error, setError] = useState('');

  const fetchDocNo = useCallback(async () => {
    try {
      const { data } = await api.get('/pergola/calculations/next-doc-no');
      setDocNo(data.doc_no || '');
    } catch (e) { setDocNo(''); }
  }, []);

  const fetchLookups = useCallback(async () => {
    try {
      const { data } = await api.get('/pergola/lookups');
      setAccounts(data.accounts || []);
      setPersonnel(data.personnel || []);
      setTemplates(data.templates || []);
    } catch (e) { console.error(e); }
  }, []);

  // Yeni: doc no üret. Düzenle: kayıt yükle.
  useEffect(() => {
    fetchLookups();
    if (id) {
      setLoading(true);
      api.get('/pergola/calculations/' + id).then(({ data }) => {
        setDocNo(data.doc_no || '');
        const parts = splitDateParts(data.doc_date);
        setDocDateOnly(parts.date);
        setDocTime(parts.time);
        if (data.prospect_account_id) { setAccountId(data.prospect_account_id); setSelectedIsProspect(true); }
        else { setAccountId(data.account_id || null); setSelectedIsProspect(false); }
        setManual({
          name: data.manual_account_name || '',
          phone: data.manual_phone || '',
          address: data.manual_address || '',
          email: data.manual_email || '',
        });
        setPreparedById(data.prepared_by_personnel_id || null);
        setTemplateId(data.template_id || null);
        if (data.calculation_data) {
          try {
            const parsed = typeof data.calculation_data === 'string' ? JSON.parse(data.calculation_data) : data.calculation_data;
            setInitialCalcValues(parsed);
          } catch (_) {}
        }
      }).catch(e => setError('Kayıt yüklenemedi'))
        .finally(() => setLoading(false));
    } else {
      fetchDocNo();
    }
  }, [id, fetchLookups, fetchDocNo]);

  const saveAsProspect = async () => {
    if (!manual.name.trim()) { setError('Müşteri adı zorunludur'); return; }
    setSavingProspect(true); setError('');
    try {
      const r = await api.post('/prospect-accounts', {
        title: manual.name.trim(),
        phone: manual.phone.trim() || null,
        email: manual.email.trim() || null,
        address: manual.address.trim() || null,
        source: 'pergola',
      });
      const newId = r.data?.id || r.data?.data?.id;
      // Lookup'ı yenile, yeni prospect listede gözüksün
      const { data: lk } = await api.get('/pergola/lookups');
      setAccounts(lk.accounts || []);
      if (newId) {
        setAccountId(newId);
        setSelectedIsProspect(true);
      }
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || 'Potansiyel cari kaydedilemedi');
    } finally {
      setSavingProspect(false);
    }
  };

  const clearAccountSelection = () => {
    setAccountId(null);
    setSelectedIsProspect(false);
    setManual({ name: '', phone: '', address: '', email: '' });
  };

  const onAccountChange = (newId) => {
    setAccountId(newId);
    const a = accounts.find(x => x.id === newId);
    if (a) {
      setSelectedIsProspect(!!a.is_prospect);
      setManual({
        name: a.name || '',
        phone: a.phone || a.mobile || '',
        address: a.address || '',
        email: a.email || '',
      });
    } else {
      setSelectedIsProspect(false);
    }
  };

  const save = async () => {
    if (!manual.name.trim()) { setError('Müşteri adı zorunludur'); return; }
    setSaving(true); setError('');
    const tpl = templates.find(t => t.id === templateId);
    try {
      const payload = {
        doc_no: docNo,
        doc_date: docDateOnly + 'T' + (docTime || '00:00'),
        // Sadece gerçek cari ise account_id yazılır; potansiyel cari seçildiyse account_id null +
        // bilgiler manual alanlardan kaydedilir. (Cariye aktarılmadığı için FK ilişkisi kurulmaz.)
        account_id: (accountId && !selectedIsProspect) ? accountId : null,
        prospect_account_id: (accountId && selectedIsProspect) ? accountId : null,
        manual_account_name: manual.name.trim(),
        manual_phone: manual.phone.trim() || null,
        manual_address: manual.address.trim() || null,
        manual_email: manual.email.trim() || null,
        prepared_by_personnel_id: preparedById || null,
        template_id: templateId || null,
        calculation_data: calcResult ? {
          // Tarihsel snapshot — Excel şablonu sonradan değişse bile bu kayıt
          // teklifin verildiği andaki tüm değerleri korur.
          template_id: templateId || null,
          template_name: tpl?.product_name || null,
          template_filename: tpl?.excel_filename || null,
          saved_at: new Date().toISOString(),
          values:  calcResult.values,
          inputs_labeled: calcResult.inputs_labeled,
          outputs: calcResult.outputs,
          summary: calcResult.summary,
          sales_price: calcResult.salesPrice,
        } : null,
        total_amount: calcResult?.salesPrice || null,
      };
      if (id) await api.put('/pergola/calculations/' + id, payload);
      else    await api.post('/pergola/calculations', payload);
      onSaved && onSaved();
    } catch (e) {
      setError(e.response?.data?.error || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="stocks-page"><div style={{ padding: 24 }}>Yükleniyor...</div></div>;

  return (
    <div className="stocks-page">
      <div className="stocks-topbar">
        <div className="stocks-topbar-left">
          <h2>{id ? `Persan Maliyet · ${docNo}` : 'Yeni Persan Maliyet Tablosu'}</h2>
        </div>
        <div className="stocks-topbar-right">
          {id && onDelete && (
            <Button variant="danger" size="sm" icon={<IconTrash />}
              disabled={saving || !!row?.converted_to_quote_id}
              title={row?.converted_to_quote_id ? `"${row.converted_to_quote_doc_no}" teklifine aktarılmış — silmek için önce teklifi silin` : 'Bu kaydı sil'}
              onClick={onDelete}>Sil</Button>
          )}
          <Button variant="ghost" size="sm" onClick={onBack} disabled={saving}>Vazgeç</Button>
          <Button size="sm" onClick={save} loading={saving} disabled={!!row?.converted_to_quote_id}>Kaydet</Button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', padding: 16, display: 'grid', gap: 16, alignContent: 'start' }}>
        {error && (
          <div className="ui-field-error" style={{ padding: 10, borderRadius: 6, background: 'rgba(220,38,38,0.08)' }}>{error}</div>
        )}

        {/* ── Belge bilgileri ── */}
        <section style={cardStyle}>
          <div style={cardHeader}>Belge Bilgileri</div>
          <div style={gridRow(180)}>
            <FormField label="Belge No" required>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type="text" value={docNo} readOnly style={{ flex: 1, background: 'var(--bg-secondary, #f9f9f9)' }} />
                {!id && (
                  <Button variant="ghost" size="sm" iconOnly icon={<IconRefresh />}
                    title="Belge no'yu yenile" onClick={fetchDocNo} />
                )}
              </div>
            </FormField>
            <FormField label="Tarih" required>
              <input type="date" value={docDateOnly} onChange={e => setDocDateOnly(e.target.value)} />
            </FormField>
            <FormField label="Saat" required>
              <input type="time" value={docTime} onChange={e => setDocTime(e.target.value)} />
            </FormField>
            <FormField label="Hazırlayan Personel">
              <SearchableSelect
                value={preparedById}
                onChange={setPreparedById}
                options={personnel}
                getLabel={(p) => p.name}
                getSubLabel={(p) => p.code}
                searchKeys={['name', 'code']}
                placeholder="Personel seçin..."
                emptyText="Personel yok"
              />
            </FormField>
          </div>
        </section>

        {/* ── Cari bilgileri ── */}
        <section style={cardStyle}>
          <div style={cardHeader}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconUser /> Müşteri Bilgileri</span>
            <div style={{ marginLeft: 'auto' }}>
              <Button size="sm" variant="ghost" onClick={saveAsProspect} loading={savingProspect}
                disabled={!manual.name.trim() || !!accountId}
                title={
                  accountId ? 'Önce mevcut seçimi "Temizle" butonuyla kaldırın'
                  : manual.name.trim() ? 'Aşağıdaki müşteri bilgilerini Potansiyel Cari olarak kaydet'
                  : 'Önce aşağıdaki Müşteri Adı alanını doldurun'
                }>
                + Yeni Potansiyel Cari
              </Button>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <FormField label="Cari / Potansiyel Cari" hint="Cari ve henüz cariye aktarılmamış potansiyel cariler aynı listededir. Seçtiğinizde aşağıdaki bilgiler otomatik dolar.">
              <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <SearchableSelect
                    value={accountId}
                    onChange={onAccountChange}
                    options={accounts}
                    getLabel={(a) => a.is_prospect ? `${a.name || ''} 🌟` : (a.name || '')}
                    getSubLabel={(a) => a.is_prospect ? `Potansiyel · ${a.code || ''}` : (a.code || '')}
                    getRightLabel={(a) => a.phone || a.mobile || ''}
                    searchKeys={['name', 'code', 'phone', 'mobile']}
                    placeholder="Cari ara veya seç..."
                    emptyText="Cari yok"
                  />
                </div>
                {accountId && (
                  <Button size="sm" variant="ghost" onClick={clearAccountSelection}
                    title="Seçimi temizle ve formu sıfırla">
                    ✕ Temizle
                  </Button>
                )}
              </div>
            </FormField>
          </div>

          <div style={gridRow(240)}>
            <FormField label="Müşteri Adı" required>
              <input type="text" value={manual.name}
                onChange={e => setManual({ ...manual, name: e.target.value })}
                placeholder="Ad Soyad veya Firma Ünvanı" />
            </FormField>
            <FormField label="Telefon">
              <input type="tel" value={manual.phone}
                onChange={e => setManual({ ...manual, phone: e.target.value })}
                placeholder="0 (5xx) xxx xx xx" />
            </FormField>
          </div>
          <div style={gridRow(240)}>
            <FormField label="E-posta">
              <input type="email" value={manual.email}
                onChange={e => setManual({ ...manual, email: e.target.value })}
                placeholder="ornek@firma.com" />
            </FormField>
            <FormField label="Adres">
              <input type="text" value={manual.address}
                onChange={e => setManual({ ...manual, address: e.target.value })}
                placeholder="Açık adres" />
            </FormField>
          </div>

        </section>

        {/* ── Pergole Şablonu ── */}
        <section style={cardStyle}>
          <div style={cardHeader}>📐 Pergole Şablonu</div>
          <FormField label="Şablon" hint="Hesaplamada kullanılacak Excel formül dosyasının bağlı olduğu pergole modeli">
            <SearchableSelect
              value={templateId}
              onChange={setTemplateId}
              options={templates}
              getLabel={(t) => t.product_name}
              getSubLabel={(t) => t.excel_filename || '— dosya yok'}
              searchKeys={['product_name', 'excel_filename']}
              placeholder="Şablon seçin..."
              emptyText="Şablon yok — önce Persan Tablo Şablonları'nda ekleyin"
            />
          </FormField>

          {(() => {
            const tpl = templates.find(t => t.id === templateId);
            if (!tpl) return null;
            return (
              <div style={{
                marginTop: 12, padding: 12,
                background: 'var(--bg-secondary, #f6f7f9)',
                border: '1px solid var(--border-color, #e5e5e5)',
                borderRadius: 6,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
                gap: 12,
                fontSize: 13,
              }}>
                <div>
                  <div style={infoLabel}>Ürün Adı</div>
                  <div style={infoValue}><strong>{tpl.product_name}</strong></div>
                </div>
                <div>
                  <div style={infoLabel}>Excel Dosyası</div>
                  <div style={infoValue}>
                    {tpl.excel_filename ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: 'var(--accent-primary, #16a34a)' }}>📄</span>
                        {tpl.excel_filename}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>— dosya yok</span>
                    )}
                  </div>
                </div>
                <div>
                  <div style={infoLabel}>Şablon Eklenme Tarihi</div>
                  <div style={infoValue}>{fmtDate(tpl.created_at)}</div>
                </div>
              </div>
            );
          })()}
        </section>

        {/* ── Kayıtlı Snapshot (sadece düzenlemede ve veri varsa) ── */}
        {id && initialCalcValues && initialCalcValues.summary && initialCalcValues.summary.length > 0 && (
          <section style={{ ...cardStyle, background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(168,85,247,0.08))', borderColor: 'rgba(99,102,241,0.3)' }}>
            <div style={cardHeader}>
              📌 Kayıtlı Değerler — Bu Teklif Verildiğinde
              {initialCalcValues.saved_at && (
                <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 12, color: 'var(--text-tertiary)' }}>
                  ({new Date(initialCalcValues.saved_at).toLocaleString('tr-TR')})
                </span>
              )}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
              gap: 12, fontSize: 13,
            }}>
              {initialCalcValues.template_name && (
                <div>
                  <div style={infoLabel}>Kullanılan Şablon</div>
                  <div style={infoValue}>
                    <strong>{initialCalcValues.template_name}</strong>
                    {initialCalcValues.template_filename && (
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>📊 {initialCalcValues.template_filename}</div>
                    )}
                  </div>
                </div>
              )}
              {initialCalcValues.summary
                .filter(s => ['SATIŞ FİYATI', 'KAR %', 'EURO SATIŞ', 'DOLAR SATIŞ'].includes(s.label))
                .map(s => {
                  const isPercent = s.label === 'KAR %' || !!s.percent;
                  const unit = isPercent ? ' %' : ' ₺';
                  return (
                    <div key={s.label}>
                      <div style={infoLabel}>{s.label}</div>
                      <div style={{
                        ...infoValue,
                        fontSize: s.primary ? 18 : 14,
                        fontWeight: s.primary ? 700 : 600,
                        color: s.primary ? 'var(--accent-primary, #2563eb)' : undefined,
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                        {typeof s.value === 'number'
                          ? new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(s.value)
                          : '—'}
                        {typeof s.value === 'number' && unit}
                      </div>
                    </div>
                  );
                })}
            </div>
            <div style={{
              marginTop: 10, fontSize: 11, color: 'var(--text-tertiary)',
              borderTop: '1px solid var(--border-color, #e5e5e5)', paddingTop: 8,
            }}>
              💡 Bu değerler teklif verildiği andaki Excel şablonu ile hesaplandı. Aşağıdaki canlı hesap, mevcut şablon değişmişse farklı sonuç verebilir.
            </div>
          </section>
        )}

        {/* ── Hesap formu (Excel motoru) ── */}
        <section style={{ ...cardStyle, padding: 16 }}>
          <div style={cardHeader}>🧮 Hesap Formülü {id && initialCalcValues ? '(Canlı — mevcut şablonla)' : ''}</div>
          <ExcelCalcForm
            templateId={templateId}
            initialValues={initialCalcValues}
            onChange={setCalcResult}
          />
        </section>
      </div>

    </div>
  );
}

// (Eski tek-belge TransferToQuoteModal kaldırıldı — çoklu seçim listeden yapılıyor)
function _RemovedTransferToQuoteModal_NotUsed({ calcId, summary, docNo, customerName, templateName, onClose, onTransferred }) {
  const findVal = (label) => {
    const it = (summary || []).find(s => s.label === label);
    return (it && typeof it.value === 'number') ? it.value : null;
  };
  const tryPrice = findVal('SATIŞ FİYATI');
  const usdPrice = findVal('DOLAR SATIŞ');
  const eurPrice = findVal('EURO SATIŞ');

  const [currency, setCurrency] = useState('TRY');
  const [exchangeRate, setExchangeRate] = useState(1);
  const [rateLoading, setRateLoading] = useState(false);
  const [manualRate, setManualRate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  // Yeni alanlar
  const defaultItemName = (templateName || 'Pergole') + (customerName ? ' — ' + customerName : '');
  const [itemName, setItemName] = useState(defaultItemName);
  const [vatIncluded, setVatIncluded] = useState(false);
  const [vatRate, setVatRate] = useState(20);

  // Para birimi değişince kuru otomatik çek
  useEffect(() => {
    setError('');
    setManualRate(false);
    if (currency === 'TRY') { setExchangeRate(1); return; }
    setRateLoading(true);
    (async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const r = await api.get('/exchange/rates/lookup', { params: { date: today, currency_code: currency } });
        if (r.data.success && r.data.data) {
          const d = r.data.data;
          // exchange_rates tablo kolonları: mid_rate, selling_rate, buying_rate (rate kolonu yok)
          const rate = parseFloat(d.mid_rate) || parseFloat(d.selling_rate) || parseFloat(d.buying_rate) || parseFloat(d.rate);
          if (rate > 0) {
            setExchangeRate(rate);
          } else {
            setExchangeRate(0);
            setError(`${currency} için kur 0 — manuel girin`);
            setManualRate(true);
          }
        } else {
          setExchangeRate(0);
          setError(`${currency} için sistemde kur bulunamadı — manuel girin`);
          setManualRate(true);
        }
      } catch (e) {
        setExchangeRate(0);
        setError(`Kur sorgulanamadı — manuel girin`);
        setManualRate(true);
      } finally { setRateLoading(false); }
    })();
  }, [currency]);

  const priceForCurrency = currency === 'TRY' ? tryPrice : currency === 'USD' ? usdPrice : eurPrice;
  const isPriceAvailable = priceForCurrency != null && priceForCurrency > 0;

  const submit = async () => {
    if (!isPriceAvailable) { setError(`Hesaplama tablosunda ${currency} fiyatı bulunamadı`); return; }
    if (currency !== 'TRY' && (!exchangeRate || exchangeRate <= 0)) { setError('Geçerli bir kur girin'); return; }
    setSubmitting(true); setError('');
    try {
      const r = await api.post(`/pergola/calculations/${calcId}/transfer-to-quote`, {
        currency_code: currency,
        exchange_rate: manualRate ? exchangeRate : undefined,
        item_name: itemName.trim() || undefined,
        vat_included: vatIncluded,
        vat_rate: (() => { const v = parseFloat(vatRate); return isNaN(v) ? 20 : v; })(),
      });
      if (r.data.success) {
        setResult(r.data.data);
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Aktarım başarısız');
    } finally { setSubmitting(false); }
  };

  if (result) {
    return (
      <Modal
        title="✓ Verilen Teklif Oluşturuldu"
        size="sm"
        onClose={onTransferred}
        closeOnOverlay={false}
        primaryAction={{ label: 'Tamam', onClick: onTransferred }}
      >
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ padding: '10px 14px', background: 'rgba(22,163,74,0.1)', borderRadius: 8, fontSize: '0.88rem' }}>
            <strong>Belge No:</strong> <code style={{ background: 'var(--bg-secondary)', padding: '2px 6px', borderRadius: 4 }}>{result.document_number || result.quote_id}</code>
          </div>
          <table style={{ width: '100%', fontSize: '0.85rem' }}>
            <tbody>
              <tr><td style={{ color: 'var(--text-tertiary)', padding: '4px 0' }}>Para Birimi:</td><td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{result.currency_code}</td></tr>
              <tr><td style={{ color: 'var(--text-tertiary)', padding: '4px 0' }}>Kur:</td><td style={{ fontFamily: 'monospace' }}>{parseFloat(result.exchange_rate).toFixed(4)}</td></tr>
              <tr><td style={{ color: 'var(--text-tertiary)', padding: '4px 0' }}>Birim Fiyat:</td><td style={{ fontFamily: 'monospace' }}>{parseFloat(result.unit_price).toFixed(2)} {result.currency_code}</td></tr>
              <tr><td style={{ color: 'var(--text-tertiary)', padding: '4px 0' }}>Genel Toplam:</td><td style={{ fontFamily: 'monospace', fontWeight: 700 }}>{parseFloat(result.grand_total).toFixed(2)} {result.currency_code}</td></tr>
            </tbody>
          </table>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>
            💡 Verilen Teklifler menüsünden bu teklifi açıp düzenleyebilirsiniz.
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Verilen Teklife Aktar"
      subtitle={<>Persan Maliyet: {docNo}{customerName ? ' · ' + customerName : ''}</>}
      size="sm"
      onClose={() => !submitting && onClose()}
      closeOnOverlay={false}
      primaryAction={{ label: 'Teklifi Oluştur', onClick: submit, loading: submitting, disabled: !isPriceAvailable || (currency !== 'TRY' && !exchangeRate) }}
      cancelAction={{ label: 'Vazgeç', onClick: onClose, disabled: submitting }}
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          Teklif para birimini seçin. Sistem hesaplama tablosundan o para birimindeki fiyatı alır ve güncel kuru otomatik uygular.
        </div>

        <FormField label="Ürün Adı *" hint="Teklif satırında görünecek ürün açıklaması">
          <input value={itemName} onChange={e => setItemName(e.target.value)} placeholder="örn: Pergole Modeli — Müşteri Adı" />
        </FormField>

        <FormField label="Para Birimi *">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {[
              { code: 'TRY', label: '₺ TRY', price: tryPrice },
              { code: 'USD', label: '$ USD', price: usdPrice },
              { code: 'EUR', label: '€ EUR', price: eurPrice },
            ].map(opt => {
              const enabled = opt.price != null && opt.price > 0;
              const active = currency === opt.code;
              return (
                <button key={opt.code} type="button"
                  onClick={() => enabled && setCurrency(opt.code)}
                  disabled={!enabled}
                  style={{
                    padding: '12px 8px', borderRadius: 8,
                    border: '2px solid ' + (active ? 'var(--accent-primary)' : 'var(--border-secondary)'),
                    background: active ? 'rgba(59,130,246,0.08)' : (enabled ? 'var(--bg-card)' : 'var(--bg-secondary)'),
                    color: enabled ? 'var(--text-primary)' : 'var(--text-tertiary)',
                    cursor: enabled ? 'pointer' : 'not-allowed',
                    opacity: enabled ? 1 : 0.5,
                    textAlign: 'center',
                  }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 4 }}>{opt.label}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: active ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>
                    {enabled ? new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(opt.price) : '—'}
                  </div>
                </button>
              );
            })}
          </div>
        </FormField>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <FormField label="KDV Oranı (%)" hint="Hesaplamada kullanılır">
            <select value={vatRate} onChange={e => setVatRate(parseFloat(e.target.value))}>
              <option value={0}>%0 (KDV'siz)</option>
              <option value={1}>%1</option>
              <option value={10}>%10</option>
              <option value={20}>%20</option>
            </select>
          </FormField>
          <FormField label="Fiyat KDV durumu" hint={vatIncluded ? 'Birim fiyat KDV dahil' : 'Birim fiyata KDV eklenecek'}>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button"
                onClick={() => setVatIncluded(false)}
                style={{
                  flex: 1, padding: '7px 10px', borderRadius: 6,
                  border: '1px solid ' + (!vatIncluded ? 'var(--accent-primary)' : 'var(--border-secondary)'),
                  background: !vatIncluded ? 'rgba(59,130,246,0.10)' : 'var(--bg-input)',
                  color: !vatIncluded ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  cursor: 'pointer', fontSize: '0.82rem', fontWeight: !vatIncluded ? 600 : 400,
                }}>KDV Hariç</button>
              <button type="button"
                onClick={() => setVatIncluded(true)}
                style={{
                  flex: 1, padding: '7px 10px', borderRadius: 6,
                  border: '1px solid ' + (vatIncluded ? 'var(--accent-primary)' : 'var(--border-secondary)'),
                  background: vatIncluded ? 'rgba(59,130,246,0.10)' : 'var(--bg-input)',
                  color: vatIncluded ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  cursor: 'pointer', fontSize: '0.82rem', fontWeight: vatIncluded ? 600 : 400,
                }}>KDV Dahil</button>
            </div>
          </FormField>
        </div>

        {currency !== 'TRY' && (
          <FormField label={`Kur (${currency} → TRY)`} hint={manualRate ? 'Manuel girildi' : (rateLoading ? 'Yükleniyor...' : 'Bugünkü sistem kuru otomatik')}>
            <div style={{ display: 'flex', gap: 6 }}>
              <input type="number" step="0.0001" min="0"
                value={exchangeRate}
                onChange={e => { setExchangeRate(parseFloat(e.target.value) || 0); setManualRate(true); }}
                style={{ flex: 1, fontFamily: 'monospace' }} />
              {!manualRate && (
                <Button size="sm" variant="ghost" onClick={() => { setManualRate(true); }}>Düzenle</Button>
              )}
            </div>
          </FormField>
        )}

        {isPriceAvailable && (
          <div style={{ padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: '0.85rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
              <span style={{ color: 'var(--text-tertiary)' }}>Birim Fiyat:</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 600, textAlign: 'right' }}>
                {new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(priceForCurrency)} {currency}
              </span>
              {currency !== 'TRY' && exchangeRate > 0 && (
                <>
                  <span style={{ color: 'var(--text-tertiary)' }}>Yerel Karşılığı:</span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 600, textAlign: 'right', color: 'var(--text-secondary)' }}>
                    ≈ {new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(priceForCurrency * exchangeRate)} TRY
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {error && (
          <div style={{ padding: '8px 12px', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 6, color: 'var(--danger)', fontSize: '0.82rem' }}>
            {error}
          </div>
        )}

        <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
          ℹ Tek kalemlik bir teklif oluşturulur ({currency} para biriminde). KDV %20 hariç eklenir. Verilen Teklifler menüsünden açıp düzenleyebilirsiniz.
        </div>
      </div>
    </Modal>
  );
}

// ─── styles ─────────────────────────────────────────────
const cardStyle = {
  background: 'var(--bg-primary, #fff)',
  border: '1px solid var(--border-color, #e5e5e5)',
  borderRadius: 8,
  padding: '12px 16px',
};
const cardHeader = {
  display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
  fontWeight: 600, fontSize: 14, color: 'var(--text-secondary, #444)',
  marginBottom: 12, paddingBottom: 8,
  borderBottom: '1px solid var(--border-color, #eee)',
};
// Auto-fit grid → büyük ekranda yan yana, mobilde otomatik alt alta sarılır.
// minPx = bir kolonun en az alacağı genişlik. Toplam alan minPx * cols altına düşerse otomatik alt satıra geçer.
const infoLabel = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-tertiary, #888)',
  marginBottom: 4,
};
const infoValue = {
  fontSize: 13,
  color: 'var(--text-primary, #222)',
};

const gridRow = (minPx = 220) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${minPx}px), 1fr))`,
  gap: 12,
  marginBottom: 8,
});
